/* eslint-disable react/prop-types */
import React, {
  useEffect, useCallback, useState,
} from 'react';
import * as PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import {
  Form, Row, Typography, Skeleton, Button, Input, PageHeader,
} from 'antd';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import { useDispatch, useSelector, connect } from 'react-redux';
import ViaValidationAlert from '../../../../components/fields/ViaValidationAlert';
import './ContactDetails.css';
import { selectAppConfig } from '../../../../state/domain/appConfig/appConfigReducer';
import { adminRoutes } from '../../../../utility/route-locations';
import AddressInput from '../../../../components/fields/AddressInput';
import { requestBusinessDetails, requestUpdateBusinessDetails } from '../../../../state/ui/application/appConfigUiActions';
import { selectSaveBusinessDetailsRequest } from '../../../../state/ui/application/appConfigUiReducer';

const { Text } = Typography;

function isEmptyAddress(address) {
  if (!address) return true;
  if ((!address.line1 || address.line1 === '')
    && (!address.line2 || address.line2 === '')
    && (!address.line3 || address.line3 === '')
    && (!address.line4 || address.line4 === '')
    && (!address.region || address.region === '')
    && (!address.zipPostcode || address.zipPostcode === '')
    && (!address.country || address.country === '')
    && (!address.countryDescription || address.countryDescription === '')) {
    return true;
  }
  return false;
}
const CompanyReferenceField = injectIntl(({ form, details, intl }) => (
  <Form.Item label={<FormattedMessage id="registeredCompanyNo" />}>
    { form.getFieldDecorator('registeredCompanyNo', {
      initialValue: details ? details.registeredCompanyNo : null,
    })(
      <Input
        type="text"
        maxLength={250}
        allowClear={!!details}
        placeholder={intl.formatMessage({ id: 'enter' })}
      />,
    )}
  </Form.Item>
));

const TaxReferenceField = injectIntl(({ form, details, intl }) => (
  <Form.Item label={<FormattedMessage id="taxRefNo" />}>
    { form.getFieldDecorator('taxReferenceNo', {
      initialValue: details ? details.taxReferenceNo : null,
    })(
      <Input
        type="text"
        maxLength={250}
        allowClear
        placeholder={intl.formatMessage({ id: 'enter' })}
      />,
    )}
  </Form.Item>
));

// Creates an address form item, decorator is address, returns an object matching address.
function AddressField({
  initialValue,
  className,
  label,
  form,
  required,
  decorator,
  ...props
}) {
  const intl = useIntl();
  const emptyAddress = {
    line1: '',
    line2: '',
    line3: '',
    line4: '',
    region: '',
    zipPostcode: '',
    country: '',
    countryDescription: '',
  };

  const [validationStatus, setValidationStatus] = useState({});

  return (
    <Form.Item required={required} label={label} className={className} validateStatus="success" {...props}>
      { form.getFieldDecorator(decorator, {
        initialValue: initialValue || emptyAddress,
        rules: [
          {
            validator(rule, value, callback) {
              let newValidationStatus = {};
              // If the address is empty, this is fine since the entire field is optional
              if (!value.country && !isEmptyAddress(value)) {
                callback(true);
                newValidationStatus = { ...newValidationStatus, country: intl.formatMessage({ id: 'addCountryRequired' }) };
              }
              if (!value.line1 && !isEmptyAddress(value)) {
                callback(true);
                newValidationStatus = { ...newValidationStatus, line1: intl.formatMessage({ id: 'addLine1Required' }) };
              }
              setValidationStatus(newValidationStatus);
              callback();
            },
          },
        ],
      })(
        <AddressInput allowClear validationStatus={validationStatus} />,
      )}
    </Form.Item>
  );
}


/**
 * The page for editing contact details of a company.
 * @param {object} props The react props.
 */
function EditCompanyDetails({
  form, history, businessDetails,
}) {
  // Redux State
  const updateRequest = useSelector(selectSaveBusinessDetailsRequest);
  const updateError = updateRequest ? updateRequest.error : null;
  const updateLoading = updateRequest && updateRequest.isLoading;
  // Redux Actions
  const dispatch = useDispatch();
  const loadPageData = useCallback(() => dispatch(requestBusinessDetails()), [dispatch]);

  // Load the initial page data.
  useEffect(() => {
    loadPageData();
  }, [loadPageData]);

  // Form submit
  const handleSubmit = async (e) => {
    e.preventDefault(); // Prevent the form from submitting traditionally.
    try {
      const values = await form.validateFields();
      // Null the addresses if they have no information so that the server knows we want to remove the addresses stored.
      // (Rather than trying to updated them to an invalid state).
      dispatch(requestUpdateBusinessDetails({
        ...values,
        businessAddress: isEmptyAddress(values.businessAddress) ? null : values.businessAddress,
        registeredAddress: isEmptyAddress(values.registeredAddress) ? null : values.registeredAddress,
      }, history));
    } catch (_) {
      // Validation has failed. Nothing for us to do.
    }
  };
  return (
    <div>
      <PageHeader
        onBack={() => history.push(adminRoutes.admin_company_details)}
        style={{ width: '100%', paddingTop: '0px' }}
        title={(<Text><FormattedMessage id="contactDetails" /></Text>)}
      />
      <div style={{ padding: '0 1em' }}>
        <ViaValidationAlert validationError={updateError} style={{ marginLeft: '60px', marginRight: '30px' }} />
        <Form onSubmit={e => handleSubmit(e)}>
          <Row>
            {businessDetails
              ? <CompanyReferenceField form={form} details={businessDetails} />
              : <Skeleton title={{ width: 100 }} active paragraph={{ width: [200], rows: 1 }} />}
          </Row>
          <Row>
            {businessDetails
              ? <TaxReferenceField form={form} details={businessDetails} />
              : <Skeleton title={{ width: 100 }} active paragraph={{ width: [200], rows: 1 }} />}
          </Row>
          <Row>
            {businessDetails
              ? <AddressField form={form} initialValue={businessDetails.businessAddress} decorator="businessAddress" label={<FormattedMessage id="businessAddress" />} />
              : <Skeleton title={{ width: 100 }} active paragraph={{ width: [200, 200, 200, 200, 100, 100, 100], rows: 7 }} />}
          </Row>
          <Row>
            {businessDetails
              ? <AddressField form={form} initialValue={businessDetails.registeredAddress} decorator="registeredAddress" label={<FormattedMessage id="registeredAddress" />} />
              : <Skeleton title={{ width: 100 }} active paragraph={{ width: [200, 200, 200, 200, 100, 100, 100], rows: 7 }} />}
          </Row>
          <Form.Item>
            <Row type="flex" justify="end" style={{ marginTop: '0.5em', marginRight: '2em' }}>
              <Button type="primary" htmlType="submit" loading={updateLoading}><FormattedMessage id="save" /></Button>
            </Row>
          </Form.Item>
        </Form>
      </div>
    </div>
  );
}

const AddressShape = PropTypes.shape({
  line1: PropTypes.string.isRequired,
  line2: PropTypes.string,
  line3: PropTypes.string,
  line4: PropTypes.string,
  region: PropTypes.string,
  zipPostcode: PropTypes.string,
  country: PropTypes.string.isRequired,
});

EditCompanyDetails.propTypes = {
  form: PropTypes.shape({
    validateFields: PropTypes.func.isRequired,
    getFieldDecorator: PropTypes.func.isRequired,
    setFieldsValue: PropTypes.func.isRequired,
    getFieldValue: PropTypes.func.isRequired,
  }).isRequired,
  history:
    PropTypes.shape({
      push: PropTypes.func.isRequired,
    }).isRequired,
  businessDetails:
    PropTypes.shape({
      businessAddress: AddressShape,
      registeredAddress: AddressShape,
      taxReferenceNo: PropTypes.string,
      registeredCompanyNo: PropTypes.string,
    }),
};

EditCompanyDetails.defaultProps = {
  businessDetails: null,
};

/**
* A function for connecting redux state to the component props.
*/
const mapStateToProps = (state) => ({
  businessDetails: selectAppConfig(state, 'businessDetails') ? selectAppConfig(state, 'businessDetails').applicationConfigValue : null,
});

export default withRouter(Form.create({ name: 'edit_company_details' })(connect(mapStateToProps)(EditCompanyDetails)));
